import { Card, CardContent, CardHeader, Divider } from '@mui/material';
import React from 'react';
import { useSessionStorage } from 'react-use';
import { Waypoint } from 'react-waypoint';

import ConversionToggle from '@components/ConversionToggle';
import SkeletonLoader from '@components/SkeletonLoader';

import { LAB_TYPES } from '@constants/option-types/lab';
import * as ROUTES from '@constants/routes';

import { useConversion } from '@hooks/useConversion';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';
import { LabResultObservationRecord } from '@models/lab-result';

import { convertUnit, getUnit } from '@utils/conversion';

import { useGetLabResultResourcesLazyQuery } from 'generated/diagnostic-report.generated';

import { GridBody } from './LabResultsGrid.style';
import { LabResultsCell } from './index';

export default function LabResultsGrid({
  response,
  title = '',
  type = '',
  subtitle = '',
}: // group = '',
{
  group: string;
  response: DiagnosticReportRecord;
  subtitle: string;
  title?: string | null;
  type: string;
}) {
  const { details, resultCount, id: record_id } = response;
  const [token] = useSessionStorage('share_token');

  const {
    state: { conversion },
  } = useConversion();
  const {
    state: { activeProfile, isAuthenticated },
  } = useSession();
  const { navigateRoute } = useNavigate();

  const patient_id = response.patient ?? activeProfile?.patient_id ?? '';

  const [enabled, setEnabled] = React.useState(false);
  const [query, { data, loading: dataLoading }] =
    useGetLabResultResourcesLazyQuery({
      ...(!isAuthenticated && {
        context: {
          headers: {
            authorization: token,
          },
        },
      }),
    });

  const results = (data?.getLabReport.resultResources ?? []).map(
    (result) => new LabResultObservationRecord(result)
  );

  const loading =
    dataLoading || Boolean(enabled && !results.length && resultCount);

  // Only query for results if lab section is in view for 0.5s or greater
  // Improves performance and reduces load on server when user scrolls quickly through list of labs
  React.useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (enabled && record_id && patient_id) {
        query({
          variables: {
            input: {
              patient_id,
              record_id,
            },
          },
        });
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, patient_id]);

  const handleEnter = () => {
    setEnabled(true);
  };

  const handleLeave = () => {
    setEnabled(false);
  };

  function getValue(
    value: boolean | number | string | null | undefined,
    unit: string | null | undefined,
    type: string,
    table: string
  ) {
    const conversionTableName = type.split('|').at(1) ?? '';
    const conversionTable = conversion[conversionTableName];
    if (value === null || value === undefined) {
      return '';
    }
    if (!unit) {
      return value;
    }

    try {
      const numValue = parseFloat(value.toString());
      return (
        <>
          {convertUnit(
            numValue,
            unit,
            conversion[type] ? 'CON' : 'SI',
            2,
            table
          )}{' '}
          {getUnit(unit, conversionTable ? 'CON' : 'SI', table)}
        </>
      );
    } catch {
      return (
        <>
          {value} {unit}
        </>
      );
    }
  }

  const handleNavigate = (code: string, title: string) => {
    if (code === 'other') {
      return;
    }
    navigateRoute(ROUTES.LAB_RESULTS_REPORT_DETAIL, {
      category: type,
      code,
      title,
    });
  };

  return (
    <Card>
      <CardHeader
        title={title}
        subheader={subtitle}
        action={<ConversionToggle group={LAB_TYPES} />}
      />
      <Divider />
      <Waypoint onEnter={handleEnter} onLeave={handleLeave}>
        <CardContent>
          <GridBody>
            <SkeletonLoader
              count={response.resultCount}
              loading={loading}
              width="100%"
              sx={{ minHeight: '80px' }}
            >
              {results.map(({ value, unit, code, displayName }, index) => {
                return (
                  <LabResultsCell
                    date={response.getTestDateStr()}
                    key={`observation-${index}`}
                    onClick={
                      isAuthenticated
                        ? () =>
                            handleNavigate(code, displayName ?? 'Lab Results')
                        : undefined
                    }
                    title={displayName}
                    value={getValue(value, unit, type, code)}
                  />
                );
              })}
            </SkeletonLoader>
          </GridBody>
        </CardContent>
      </Waypoint>

      {details ? (
        <>
          <Divider />
          <CardContent>{details}</CardContent>
        </>
      ) : null}
    </Card>
  );
}
